import { createFileRoute } from "@tanstack/react-router";
import {
	Bot,
	CalendarClock,
	GitBranch,
	MessageSquare,
	Plug,
	Sparkles,
	Workflow,
} from "lucide-react";
import { FeatureGrid } from "@/components/feature-grid";
import { MarketingHero } from "@/components/marketing-hero";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { MarketingSection } from "@/components/marketing-section";
import { PromptCalendarExample } from "@/components/prompt-calendar-example";
import { HomepageAgentInstall } from "@/components/setup/homepage-agent-install";
import { buildPageHead } from "@/lib/seo-meta";

const path = "/mcp-server";

export const Route = createFileRoute(path)({
	component: McpMarketing,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: path,
			title: "AgentReacher | MCP for Social Media",
			description:
				"AgentReacher's Model Context Protocol server turns Claude, ChatGPT, Cursor, and Codex into AI social media agents that draft, schedule, and publish from chat.",
		}),
});

function McpMarketing() {
	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="Model Context Protocol"
				title="MCP for social media agents."
				description="AgentReacher gives MCP-compatible assistants the tools to create posts, adapt them per network, schedule the calendar, and publish after approval."
				primaryCta={{ label: "Get the MCP server", to: "/signup" }}
				secondaryCta={{ label: "How it works", to: "/docs/mcp" }}
			/>

			<MarketingSection
				title="What MCP changes"
				description="Your AI assistant goes from text generator to publishing operator: it can inspect the calendar, make changes, and queue real posts."
			>
				<FeatureGrid
					features={[
						{
							icon: Plug,
							title: "One server, every assistant",
							description:
								"Connect once. The same MCP tools work in Claude Desktop, ChatGPT, Cursor, and any other MCP host.",
						},
						{
							icon: Bot,
							title: "Real publishing actions",
							description:
								"Tools that schedule, post, edit, and cancel  -  not just read-only context retrieval.",
						},
						{
							icon: CalendarClock,
							title: "Calendar in chat",
							description:
								"Ask 'what's scheduled this week?' and the assistant pulls the live queue from your workspace.",
						},
						{
							icon: Workflow,
							title: "Confirm before send",
							description:
								"Tool calls that publish go through a confirm gate. You stay in control of every post.",
						},
						{
							icon: GitBranch,
							title: "Every connected platform",
							description:
								"Bluesky, X, Threads, LinkedIn, Instagram, TikTok, YouTube, Facebook, Pinterest. One protocol.",
						},
						{
							icon: Sparkles,
							title: "Voice-aware",
							description:
								"The assistant uses your past posts as context so drafts stay in your voice.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection
				title="Live MCP prompt-to-calendar example"
				description="MCP is the bridge between an assistant that can write and an assistant that can actually operate your social media calendar."
			>
				<PromptCalendarExample
					assistant="MCP assistant"
					prompt="Schedule three posts about the new pricing page: one LinkedIn explainer, one X thread, and one Bluesky note. Avoid sounding promotional."
					response="I created platform-specific drafts, assigned publish times, and added them to the AgentReacher calendar with approval required."
					slots={[
						{
							day: "Tuesday",
							time: "09:00",
							channel: "LinkedIn",
							title:
								"Plain-English pricing rationale for founders comparing plans.",
						},
						{
							day: "Wednesday",
							time: "10:40",
							channel: "X",
							title: "Five-part thread on what changed and who each plan fits.",
						},
						{
							day: "Thursday",
							time: "13:15",
							channel: "Bluesky",
							title:
								"Short community note asking what pricing details buyers want upfront.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection title="What you can ask your assistant" align="left">
				<div className="mx-auto max-w-2xl space-y-3 text-sm leading-relaxed">
					{[
						"Schedule three LinkedIn posts about the launch for next Tuesday, Wednesday, and Thursday at 9am.",
						"What's queued on Bluesky this week? Move the Friday post to Monday.",
						"Draft a thread for X based on the new help doc and queue it for tomorrow morning.",
						"Cross-post the latest Reel to TikTok and YouTube Shorts.",
						"Pause the queue while we figure out the messaging on the partnership.",
					].map((prompt) => (
						<div
							key={prompt}
							className="flex gap-3 rounded-md border border-border bg-background p-4"
						>
							<MessageSquare className="size-4 shrink-0 text-muted-foreground" />
							<p className="text-foreground">{prompt}</p>
						</div>
					))}
				</div>
			</MarketingSection>

			<HomepageAgentInstall />
		</MarketingPageShell>
	);
}
